import {
  ActivityNotification,
  activityNotificationCancel,
  activityNotificationList,
  ActivityNotifyType,
  ActivityPublishStatus,
} from '@/api/activityMng';
import TabsTable, { TabsTableRef } from '@/components/TabsTable';
import { Button, Image, message, Space, TableColumnType, Tag } from 'antd';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CancelConfirmDialog from './cancelConfirmDialog';
import { getValueFromLink, UniversalLinkPage } from '@/utils/universalLinks';

export const ActivityStatusColorMap = {
  [ActivityPublishStatus.PUBLISHED]: 'green',
  [ActivityPublishStatus.PENDING]: 'gray',
  [ActivityPublishStatus.CANCELED]: 'red',
};

export const ActivityStatusMap = {
  [ActivityPublishStatus.PUBLISHED]: '已发布',
  [ActivityPublishStatus.PENDING]: '等待发布',
  [ActivityPublishStatus.CANCELED]: '取消发布',
  [ActivityPublishStatus.RECALLED]: '已撤回',
};

const ActivityMngPage = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const tableRef = useRef<TabsTableRef>(null);
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] =
    useState(false);
  const [cancelActivityNotificationId, setCancelActivityNotificationId] =
    useState<number>();

  const tabItems = [
    {
      key: 'all',
      label: '汇总',
    },
    {
      key: ActivityPublishStatus.PUBLISHED.toString(),
      label: '已发布',
    },
    {
      key: ActivityPublishStatus.PENDING.toString(),
      label: '等待发布',
    },
    {
      key: ActivityPublishStatus.CANCELED.toString(),
      label: '取消发布',
    },
  ];

  const handleDetailClick = (record: ActivityNotification) => {
    navigate(`/activity-mng/editor?id=${record.activityNotificationId}`);
  };

  const handleCancelClick = (record: ActivityNotification) => {
    setCancelActivityNotificationId(record.activityNotificationId);
    setIsCancelConfirmDialogOpen(true);
  };

  const columns: TableColumnType<ActivityNotification>[] = [
    {
      title: '通知ID',
      dataIndex: 'activityNotificationId',
      key: 'activityNotificationId',
    },
    {
      title: '通知预览图',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (item: string) => (
        <Image src={item} width={100} preview={false} />
      ),
    },
    {
      title: '活动标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '通知类型',
      dataIndex: 'notifyType',
      key: 'notifyType',
      render: (item: ActivityNotifyType) => {
        if (item === ActivityNotifyType.LINK) {
          return '页面链接';
        }
        if (item === ActivityNotifyType.BROWSER) {
          return '外部浏览器链接';
        }
        if (item === ActivityNotifyType.TAG) {
          return '话题详情页';
        }
        if (item === ActivityNotifyType.ARTWORK) {
          return '作品详情页';
        }
        if (item === ActivityNotifyType.CONTEST) {
          return '比赛详情页';
        }
        if (item === ActivityNotifyType.NEWCOMER_CONTEST) {
          return '新人王链接跳转';
        }
        return '未知';
      },
    },
    {
      title: '活动链接',
      dataIndex: 'link',
      key: 'link',
      render: (item: string, record: ActivityNotification) => {
        let linkValue = '';
        if (record.notifyType === ActivityNotifyType.LINK) {
          linkValue = getValueFromLink(item, UniversalLinkPage.webview);
        } else if (record.notifyType === ActivityNotifyType.TAG) {
          linkValue = getValueFromLink(item, UniversalLinkPage.tagDetail);
        } else if (record.notifyType === ActivityNotifyType.ARTWORK) {
          linkValue = getValueFromLink(item, UniversalLinkPage.artworkDetail);
        } else if (record.notifyType === ActivityNotifyType.CONTEST) {
          linkValue = getValueFromLink(item, UniversalLinkPage.contestDetail);
        } else if (record.notifyType === ActivityNotifyType.NEWCOMER_CONTEST) {
          linkValue = getValueFromLink(item, UniversalLinkPage.rookieKingDetail);
        } else {
          linkValue = item;
        }
        return (
          <div
            style={{
              display: 'inline-block',
              width: 300,
              wordBreak: 'break-all',
            }}
          >
            {linkValue}
          </div>
        );
      },
    },
    {
      title: '通知发布时间',
      dataIndex: 'publishTime',
      key: 'publishTime',
    },
    {
      title: '通知状态',
      dataIndex: 'publishStatus',
      key: 'publishStatus',
      render: (item: ActivityPublishStatus) => (
        <Tag color={ActivityStatusColorMap[item]}>
          {ActivityStatusMap[item]}
        </Tag>
      ),
    },
    {
      title: '发布人',
      dataIndex: 'publishUser',
      key: 'publishUser',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space direction="vertical">
          <Button type="link" onClick={() => handleDetailClick(record)}>
            查看详情
          </Button>
          {record.publishStatus === ActivityPublishStatus.PENDING && (
            <Button
              type="link"
              onClick={() => handleCancelClick(record)}
              danger
            >
              取消发布
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const queryFn = async (page: number, pageSize: number, tabKey?: string) => {
    const resp = await activityNotificationList({
      currentPage: page,
      pageSize,
      publishStatus: tabKey === 'all' ? undefined : Number(tabKey),
    });
    if (resp.status !== 200) {
      return {
        total: 0,
        currentPage: page,
        pageSize: pageSize,
        records: [],
      };
    }
    return resp.data;
  };

  const handleCancelConfirmDialogOk = async () => {
    if (!cancelActivityNotificationId) {
      return;
    }
    const resp = await activityNotificationCancel(cancelActivityNotificationId);
    if (resp.status !== 200) {
      messageApi.error('取消发布失败: ' + resp.message);
      return;
    }
    messageApi.success('取消发布成功');
    setIsCancelConfirmDialogOpen(false);
    tableRef.current?.refresh();
  };

  return (
    <div>
      {contextHolder}
      <Button type="primary" onClick={() => navigate('/activity-mng/editor')}>
        创建活动
      </Button>
      <div style={{ marginTop: 10 }}>
        <TabsTable
          ref={tableRef}
          defaultTabKey="all"
          tabItems={tabItems}
          columns={columns}
          rowKey="activityNotificationId"
          queryKey="activityMng"
          queryFn={queryFn}
          pageSize={20}
        />
      </div>
      <CancelConfirmDialog
        isOpen={isCancelConfirmDialogOpen}
        onOk={handleCancelConfirmDialogOk}
        onCancel={() => setIsCancelConfirmDialogOpen(false)}
      />
    </div>
  );
};

export default ActivityMngPage;
